package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.data.dto.ClubDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.navigation.Routes
import com.arena_web.ui.components.forms.DatePicker
import com.arena_web.ui.components.events.CreateEventPopup
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.components.events.PopupSlideUp
import com.arena_web.ui.sections.arena.*
import com.arena_web.ui.sections.arena.mobile.ArenaContentMobile
import com.arena_web.ui.sections.events.EventsEmptyState
import com.arena_web.utils.LeafletMap
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.arena_web.utils.cachedCityCountryCode
import com.arena_web.utils.countryCodeToFlagEmoji
import com.arena_web.utils.getIpLocation
import com.arena_web.utils.getUserCoordinates
import com.arena_web.utils.lookupCityCountryCode
import com.arena_web.utils.reverseGeocodeCity
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.graphics.Color as KobwebColor
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.arena_web.ui.localization.AppStrings
import kotlin.math.abs
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.w3c.dom.HTMLElement
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import org.jetbrains.compose.web.css.*

private const val PAGE_SIZE = 10

@Page("/arena")
@Composable
fun ArenaPage() {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val ctx = rememberPageContext()

    // Tab state — restore from cache
    // val cachedTab = remember { Storage.get(StorageKeys.ARENA_TAB) }
    var activeTab by remember { mutableStateOf("Events") }

    var allEvents by remember { mutableStateOf<List<ArenaEvent>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(0) }
    var hasMore by remember { mutableStateOf(true) }
    var showCreatePopup by remember { mutableStateOf(false) }
    var showMapPopup by remember { mutableStateOf(false) }
    var showAuthGate by remember { mutableStateOf(false) }
    var reloadTrigger by remember { mutableStateOf(0) }
    val isLoggedIn = Storage.get(StorageKeys.ACCESS_TOKEN) != null

    // Clubs state (disabled)
    // var allClubs by remember { mutableStateOf<List<ClubDto>>(emptyList()) }
    // var isLoadingClubs by remember { mutableStateOf(false) }
    // var isLoadingMoreClubs by remember { mutableStateOf(false) }
    // var currentClubsPage by remember { mutableStateOf(0) }
    // var hasMoreClubs by remember { mutableStateOf(true) }

    // City filter state — restore from cache immediately
    // "__all__" means user explicitly chose "All Cities"; null means no choice yet
    val cachedCity = remember { Storage.get(StorageKeys.SELECTED_CITY) }
    var availableCities by remember { mutableStateOf<List<String>>(emptyList()) }
    var cityCountryCodes by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var selectedCity by remember { mutableStateOf(if (cachedCity == "__all__") null else cachedCity) }
    var userCity by remember { mutableStateOf<String?>(null) }
    var citiesReady by remember { mutableStateOf(cachedCity != null) }
    var cityChoiceMade by remember { mutableStateOf(cachedCity != null) }

    // Community filter state — chats are loaded lazily the first time the
    // Community dropdown is opened (see `ensureCommunitiesLoaded` below).
    // The selected id/name persist in localStorage so the chip label survives
    // page reload without us needing to eagerly fetch the chat list.
    var communities by remember { mutableStateOf<List<ChatListItemDto>>(emptyList()) }
    var communitiesLoaded by remember { mutableStateOf(false) }
    var selectedCommunityId by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_COMMUNITY_ID))
    }
    var selectedCommunityName by remember {
        mutableStateOf(Storage.get(StorageKeys.SELECTED_COMMUNITY_NAME))
    }
    val ensureCommunitiesLoaded: suspend () -> Unit = ensureCommunitiesLoaded@{
        if (communitiesLoaded) return@ensureCommunitiesLoaded
        // Sniff the user's community list with one page sized large enough
        // to cover the typical join count. If a user has more than a page's
        // worth of communities the rest will sync in once they open the
        // chat tab and scroll, which is fine — this is an opportunistic
        // hydrate, not a source of truth.
        container.chatRepository?.getChats(mapOf("page" to "1", "size" to "100"))
            ?.onSuccess { page ->
            val groups = page.items.filter { it.type.uppercase() == "GROUP" }
            communities = groups
            communitiesLoaded = true
            // Reconcile the persisted selection with the fresh list:
            //   • if the community is gone (deleted / user left) → drop the filter
            //   • if it was renamed → refresh the cached name
            val currentId = selectedCommunityId
            if (currentId != null) {
                val fresh = groups.firstOrNull { it.id == currentId }
                if (fresh == null) {
                    selectedCommunityId = null
                    selectedCommunityName = null
                    Storage.remove(StorageKeys.SELECTED_COMMUNITY_ID)
                    Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                } else if (fresh.name != selectedCommunityName) {
                    selectedCommunityName = fresh.name
                    val freshName = fresh.name
                    if (freshName != null) {
                        Storage.save(StorageKeys.SELECTED_COMMUNITY_NAME, freshName)
                    } else {
                        Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                    }
                }
            }
        }
    }

    // Date filter — restore from cache; the sentinel "__all__" means the user
    // explicitly cleared the filter, null means "first visit → default to
    // today" so we don't dump every event including past ones.
    var selectedDate by remember {
        val now = kotlin.js.Date()
        val y = now.getFullYear()
        val m = (now.getMonth() + 1).toString().padStart(2, '0')
        val d = now.getDate().toString().padStart(2, '0')
        val today = "$y-$m-$d"
        val cached = Storage.get(StorageKeys.SELECTED_DATE)
        val initial = when {
            cached == null -> today
            cached == "__all__" -> null
            // A stale past date (e.g. saved yesterday) is no longer selectable —
            // fall back to today and refresh the cache.
            cached < today -> {
                Storage.save(StorageKeys.SELECTED_DATE, today)
                today
            }
            else -> cached
        }
        mutableStateOf<String?>(initial)
    }

    val scope = rememberCoroutineScope()

    // Build API params from current filters
    fun buildParams(page: Int): Map<String, String> = buildMap {
        put("page", page.toString())
        put("size", PAGE_SIZE.toString())
        val sportId = Storage.get(StorageKeys.SELECTED_SPORT_ID) ?: ""
        if (sportId.isNotBlank()) put("sportId", sportId)
        if (selectedCity != null) put("city", selectedCity!!)
        if (selectedCommunityId != null) put("chatId", selectedCommunityId!!)
        if (selectedDate != null) put("date", selectedDate!!)
    }

    fun sortEvents(events: List<ArenaEvent>): List<ArenaEvent> = events.sortedBy { event ->
        when (event.status) {
            EventStatus.IN_PROGRESS -> 0
            EventStatus.NEW -> 1
            EventStatus.COMPLETED -> 2
            EventStatus.CANCELED -> 3
        }
    }

    // Initial load + reload on filter/sport changes
    var isFirstLoad by remember { mutableStateOf(true) }
    LaunchedEffect(reloadTrigger, selectedCity, selectedCommunityId, selectedDate) {
        // A filter change means the next mount shouldn't restore the previous
        // scroll position (it belongs to the previous result set). The very
        // first effect run is the page-mount load — keep the saved value so
        // returning from event details still restores correctly.
        if (!isFirstLoad) {
            kotlinx.browser.window.sessionStorage.removeItem("arena_mobile_events_scroll_top")
            kotlinx.browser.window.sessionStorage.removeItem("arena_desktop_events_scroll_top")
        }
        isFirstLoad = false
        isLoading = true
        currentPage = 0
        hasMore = true
        val result = container.publicEventRepository.getEvents(buildParams(0))
        result.onSuccess { page ->
            allEvents = sortEvents(page.events.map { it.toArenaEvent() })
            hasMore = page.events.size >= PAGE_SIZE
            currentPage = 0
        }.onFailure {
            console.error("Failed to load events: ${it.message}")
        }
        isLoading = false
    }

    // Load next page
    val loadMore: () -> Unit = {
        if (hasMore && !isLoadingMore && !isLoading) {
            val nextPage = currentPage + 1
            isLoadingMore = true
            scope.launch {
                val result = container.publicEventRepository.getEvents(buildParams(nextPage))
                result.onSuccess { page ->
                    val newEvents = page.events.map { it.toArenaEvent() }
                    allEvents = sortEvents(allEvents + newEvents)
                    currentPage = nextPage
                    hasMore = newEvents.size >= PAGE_SIZE
                }.onFailure {
                    console.error("Failed to load more events: ${it.message}")
                }
                isLoadingMore = false
            }
        }
    }

    // Build club params with city + sport filter (disabled)
    // fun buildClubParams(page: Int): Map<String, String> = buildMap {
    //     put("page", page.toString())
    //     put("size", PAGE_SIZE.toString())
    //     val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME) ?: ""
    //     if (sportName.isNotBlank()) put("type", sportName.uppercase())
    //     if (selectedCity != null) put("city", selectedCity!!)
    // }

    // Load clubs when tab switches to "Clubs", city or sport changes (disabled)
    // LaunchedEffect(activeTab, selectedCity, reloadTrigger) {
    //     if (activeTab == "Clubs") {
    //         isLoadingClubs = true
    //         currentClubsPage = 0
    //         hasMoreClubs = true
    //         container.clubPublicRepository.browseClubs(buildClubParams(0))
    //             .onSuccess { page ->
    //                 allClubs = page.clubs
    //                 hasMoreClubs = page.clubs.size >= PAGE_SIZE
    //                 currentClubsPage = 0
    //             }
    //             .onFailure {
    //                 console.error("Failed to load clubs: ${it.message}")
    //             }
    //         isLoadingClubs = false
    //     }
    // }

    // Load next page of clubs (disabled)
    // val loadMoreClubs: () -> Unit = {
    //     if (hasMoreClubs && !isLoadingMoreClubs && !isLoadingClubs) {
    //         val nextPage = currentClubsPage + 1
    //         isLoadingMoreClubs = true
    //         scope.launch {
    //             container.clubPublicRepository.browseClubs(buildClubParams(nextPage))
    //                 .onSuccess { page ->
    //                     allClubs = allClubs + page.clubs
    //                     currentClubsPage = nextPage
    //                     hasMoreClubs = page.clubs.size >= PAGE_SIZE
    //                 }
    //                 .onFailure {
    //                     console.error("Failed to load more clubs: ${it.message}")
    //                 }
    //             isLoadingMoreClubs = false
    //         }
    //     }
    // }

    // Communities are now loaded lazily — see `ensureCommunitiesLoaded()` wired
    // into the Community filter's `onOpen` callback further below.

    // Load cities and detect geolocation in parallel, then build the list
    LaunchedEffect(reloadTrigger) {
        // Launch geo detection and city fetching concurrently
        var detected: String? = null
        val geoJob = launch {
            // GPS when permitted, IP-derived otherwise (same fallback the
            // mobile Arena tab uses) — a denied prompt must not leave the
            // city undetected.
            val pt = getUserCoordinates()?.let { it.latitude to it.longitude }
                ?: getIpLocation()?.let { it.lat to it.lng }
            if (pt != null) {
                detected = reverseGeocodeCity(pt.first, pt.second)
            }
        }

        // Lightweight cities endpoint — returns distinct city names for today's
        // and upcoming events only (no past-day clutter), much cheaper than
        // fetching 200 full event DTOs just to extract a string list.
        // Sport-agnostic: pull cities across every sport (including "Other"
        // custom-sport events) so switching sport doesn't empty the dropdown.
        val eventCities = container.publicEventRepository
            .getUpcomingCities(sportId = null)
            .getOrNull() ?: emptyList()

        // val clubCities = container.clubPublicRepository
        //     .browseClubs(mapOf("page" to "0", "size" to "200"))
        //     .getOrNull()?.clubs?.mapNotNull { club ->
        //         val parts = club.address.split(",").map { it.trim() }
        //         if (parts.size >= 3) parts[parts.size - 2] else null
        //     }?.distinct() ?: emptyList()

        // Wait for geo to finish
        geoJob.join()
        userCity = detected

        // Build sorted list: geo city first, then the rest
        val allCities = eventCities.distinct().sorted()
        val geoCity = detected
        availableCities = if (geoCity != null) {
            val match = allCities.find { it.equals(geoCity, ignoreCase = true) }
            val name = match ?: geoCity
            listOf(name) + allCities.filter { !it.equals(geoCity, ignoreCase = true) }
        } else {
            allCities
        }

        // Auto-select geo city only if user hasn't made a choice yet
        if (selectedCity == null && !cityChoiceMade && geoCity != null) {
            selectedCity = availableCities.first()
            Storage.save(StorageKeys.SELECTED_CITY, availableCities.first())
            cityChoiceMade = true
        }
        // Migration: older builds geocoded cities in the local language (e.g.
        // "Київ") and cached them in localStorage. Now that we force English
        // labels via `accept-language=en`, replace any cached city that no
        // longer matches a known event-city — Nominatim's English variant of
        // the same place will land in `availableCities` and the user gets
        // bumped onto the correct entry without losing the auto-detected
        // location entirely.
        val cached = selectedCity
        if (cached != null && cached != "__all__" &&
            availableCities.isNotEmpty() &&
            availableCities.none { it.equals(cached, ignoreCase = true) }
        ) {
            val replacement = geoCity?.takeIf { match ->
                availableCities.any { it.equals(match, ignoreCase = true) }
            } ?: availableCities.first()
            selectedCity = replacement
            Storage.save(StorageKeys.SELECTED_CITY, replacement)
        }
        citiesReady = true
    }

    // Country code lookup for the city dropdown. Hits a synchronous localStorage
    // cache first (instant on repeat visits), then resolves new cities via
    // Nominatim sequentially so we stay polite about the rate limit.
    LaunchedEffect(availableCities) {
        if (availableCities.isEmpty()) {
            if (cityCountryCodes.isNotEmpty()) cityCountryCodes = emptyMap()
            return@LaunchedEffect
        }
        val initial = mutableMapOf<String, String>()
        availableCities.forEach { city ->
            cachedCityCountryCode(city)?.let { initial[city] = it }
        }
        if (initial.isNotEmpty()) cityCountryCodes = initial.toMap()
        for (city in availableCities) {
            if (city in initial) continue
            val code = lookupCityCountryCode(city) ?: continue
            initial[city] = code
            cityCountryCodes = initial.toMap()
        }
    }

    // Sorted view of cities for the filter dropdown: geo-detected city stays
    // pinned to the top, then Spain, then other known countries grouped by ISO
    // code, then cities whose country couldn't be resolved.
    val sortedCities = remember(availableCities, cityCountryCodes, userCity) {
        val geo = userCity?.lowercase()
        availableCities.sortedWith(
            compareBy(
                { if (geo != null && it.equals(userCity, ignoreCase = true)) 0 else 1 },
                {
                    val code = cityCountryCodes[it]
                    when {
                        code == "es" -> 0
                        code != null -> 1
                        else -> 2
                    }
                },
                { cityCountryCodes[it] ?: "zzz" },
                { it.lowercase() }
            )
        )
    }
    val cityFlags = remember(cityCountryCodes) {
        cityCountryCodes.mapValues { countryCodeToFlagEmoji(it.value) }
            .filterValues { it.isNotEmpty() }
    }

    // All filtering is now server-side
    val displayedEvents = allEvents

    val mobile = isMobile()
    val sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
    val backgroundImage = AppIcons.statisticBackground(sportName)

    AppPageLayout(
        activeTab = SidebarTab.Arena,
        title = t.pageArenaSeoTitle,
        description = t.pageArenaSeoDescription,
        onSportChanged = { reloadTrigger++ },
        backgroundImage = backgroundImage,
        mobileReady = mobile,
        // Arena swaps the bell for the activity (pulse) action — the mobile
        // counterpart of the desktop "My Activity" header button.
        mobileHeaderAction = com.arena_web.ui.sections.header.MobileHeaderAction.Activity,
    ) {
        if (mobile) {
            ArenaContentMobile(
                activeTab = activeTab,
                onTabSelected = { /* activeTab = it; Storage.save(StorageKeys.ARENA_TAB, it) */ },
                events = displayedEvents,
                isLoading = isLoading,
                isLoadingMore = isLoadingMore,
                onAddEvent = {
                    if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                },
                onEventClick = { event ->
                    ctx.router.navigateTo(Routes.eventDetails(event.id))
                },
                onLoadMore = loadMore,
                onMapClick = { showMapPopup = true },
                clubs = emptyList(),
                isLoadingClubs = false,
                isLoadingMoreClubs = false,
                onLoadMoreClubs = {},
                onClubClick = {},
                filterCount = 0,
                onFilterClick = {},
                sportName = sportName,
                cities = sortedCities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = {
                    selectedCity = it
                    cityChoiceMade = true
                    if (it != null) Storage.save(StorageKeys.SELECTED_CITY, it)
                    else Storage.save(StorageKeys.SELECTED_CITY, "__all__")
                },
                communities = communities,
                selectedCommunityId = selectedCommunityId,
                onCommunitySelected = { id ->
                    selectedCommunityId = id
                    val name = id?.let { picked ->
                        communities.firstOrNull { it.id == picked }?.name
                    }
                    selectedCommunityName = name
                    if (id != null) {
                        Storage.save(StorageKeys.SELECTED_COMMUNITY_ID, id)
                        if (name != null) Storage.save(StorageKeys.SELECTED_COMMUNITY_NAME, name)
                        else Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                    } else {
                        Storage.remove(StorageKeys.SELECTED_COMMUNITY_ID)
                        Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                    }
                },
                onCommunityFilterOpen = { scope.launch { ensureCommunitiesLoaded() } },
                selectedDate = selectedDate,
                onDateSelected = {
                    selectedDate = it
                    if (it != null) Storage.save(StorageKeys.SELECTED_DATE, it)
                    else Storage.save(StorageKeys.SELECTED_DATE, "__all__")
                },
            )
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .sPadding(26, 40)
                    .sGap(24)
            ) {
                ArenaActionBar(
                    activeTab = activeTab,
                    onTabSelected = { /* activeTab = it; Storage.save(StorageKeys.ARENA_TAB, it) */ },
                    onAddEvent = {
                    if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                },
                    onAddClub = { /* container.requestBusinessSwitchHighlight() */ },
                    onMapClick = { showMapPopup = true },
                    cities = sortedCities,
                    cityFlags = cityFlags,
                    selectedCity = selectedCity,
                    onCitySelected = {
                        selectedCity = it
                        cityChoiceMade = true
                        if (it != null) Storage.save(StorageKeys.SELECTED_CITY, it)
                        else Storage.save(StorageKeys.SELECTED_CITY, "__all__")
                    },
                    citiesReady = citiesReady,
                    communities = communities,
                    selectedCommunityId = selectedCommunityId,
                    selectedCommunityName = selectedCommunityName,
                    onCommunitySelected = { id ->
                        selectedCommunityId = id
                        val name = id?.let { picked ->
                            communities.firstOrNull { it.id == picked }?.name
                        }
                        selectedCommunityName = name
                        if (id != null) {
                            Storage.save(StorageKeys.SELECTED_COMMUNITY_ID, id)
                            if (name != null) Storage.save(StorageKeys.SELECTED_COMMUNITY_NAME, name)
                            else Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                        } else {
                            Storage.remove(StorageKeys.SELECTED_COMMUNITY_ID)
                            Storage.remove(StorageKeys.SELECTED_COMMUNITY_NAME)
                        }
                    },
                    onCommunityFilterOpen = { scope.launch { ensureCommunitiesLoaded() } },
                    selectedDate = selectedDate,
                    onDateSelected = {
                    selectedDate = it
                    if (it != null) Storage.save(StorageKeys.SELECTED_DATE, it)
                    else Storage.save(StorageKeys.SELECTED_DATE, "__all__")
                }
                )

                if (activeTab == "Events") {
                    if (isLoading) {
                        Box(
                            modifier = Modifier.fillMaxWidth().flexGrow(1),
                            contentAlignment = Alignment.Center,
                        ) {
                            LoadingSpinner(size = 80.px)
                        }
                    } else if (displayedEvents.isEmpty()) {
                        EmptyEventsState(onAddEvent = {
                            if (isLoggedIn) showCreatePopup = true else showAuthGate = true
                        })
                    } else {
                        EventGrid(
                            events = displayedEvents,
                            isLoadingMore = isLoadingMore,
                            onEventClick = { event ->
                                ctx.router.navigateTo(Routes.eventDetails(event.id))
                            },
                            onLoadMore = loadMore,
                            modifier = Modifier
                                .fillMaxWidth()
                                .flexGrow(1)
                                .sPaddingRight(8)
                                .styleModifier {
                                    property("min-height", "0")
                                    property("overflow-y", "auto")
                                    property("overflow-x", "hidden")
                                    property("scrollbar-width", "thin")
                                    property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                                }
                        )
                    }
                }
                // Clubs tab content (disabled)
                // } else {
                //     if (isLoadingClubs) {
                //         Box(modifier = Modifier.fillMaxWidth().flexGrow(1))
                //     } else if (allClubs.isEmpty()) {
                //         EmptyClubsState()
                //     } else {
                //         ClubGrid(
                //             clubs = allClubs,
                //             isLoadingMore = isLoadingMoreClubs,
                //             onClubClick = { club ->
                //                 ctx.router.navigateTo(Routes.clubDetail(club.id))
                //             },
                //             onLoadMore = loadMoreClubs,
                //             modifier = Modifier
                //                 .fillMaxWidth()
                //                 .flexGrow(1)
                //                 .sPaddingRight(8)
                //                 .styleModifier {
                //                     property("min-height", "0")
                //                     property("overflow-y", "auto")
                //                     property("overflow-x", "hidden")
                //                     property("scrollbar-width", "thin")
                //                     property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                //                 }
                //         )
                //     }
                // }
            }
        }

        // Popup lives inside the layout so it inherits the LocalSportColors
        // CompositionLocal (provided by AppPageLayout).
        CreateEventPopup(
            isOpen = showCreatePopup,
            onClose = { showCreatePopup = false },
            onCreated = {
                showCreatePopup = false
                reloadTrigger++
            }
        )

        if (showMapPopup) {
            MapPopup(
                activeTab = activeTab,
                events = displayedEvents,
                clubs = emptyList(),
                onClose = { showMapPopup = false },
                onEventOpen = { event ->
                    showMapPopup = false
                    ctx.router.navigateTo(Routes.eventDetails(event.id))
                }
            )
        }

        com.arena_web.ui.components.overlay.AccountRequiredModal(
            isOpen = showAuthGate,
            onClose = { showAuthGate = false },
            onSignedIn = {
                showAuthGate = false
                kotlinx.browser.window.location.reload()
            }
        )
    }
}

// =============================================================================
// Action Bar
// =============================================================================

@Composable
private fun ArenaActionBar(
    activeTab: String = "Events",
    onTabSelected: (String) -> Unit = {},
    onAddEvent: () -> Unit = {},
    onAddClub: () -> Unit = {},
    onMapClick: () -> Unit = {},
    cities: List<String> = emptyList(),
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String? = null,
    onCitySelected: (String?) -> Unit = {},
    citiesReady: Boolean = false,
    communities: List<ChatListItemDto> = emptyList(),
    selectedCommunityId: String? = null,
    selectedCommunityName: String? = null,
    onCommunitySelected: (String?) -> Unit = {},
    onCommunityFilterOpen: () -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    // z-index lifts the entire bar into its own stacking context so the
    // filter dropdowns (which overflow below the bar) aren't covered by
    // the EventGrid that follows in the parent Column.
    Box(modifier = Modifier.fillMaxWidth().position(Position.Relative).zIndex(50)) {
        // Centered filter pills — absolutely positioned so the right-side
        // action buttons don't push them off-axis.
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("left", "50%")
                property("top", "50%")
                property("transform", "translate(-50%, -50%)")
            }
        ) {
            ActionBarFilterPills(
                activeTab = activeTab,
                cities = cities,
                cityFlags = cityFlags,
                selectedCity = selectedCity,
                onCitySelected = onCitySelected,
                citiesReady = citiesReady,
                communities = communities,
                selectedCommunityId = selectedCommunityId,
                selectedCommunityName = selectedCommunityName,
                onCommunitySelected = onCommunitySelected,
                onCommunityFilterOpen = onCommunityFilterOpen,
                selectedDate = selectedDate,
                onDateSelected = onDateSelected
            )
        }

        // Right: action buttons, anchored to the trailing edge.
        Row(
            modifier = Modifier.sGap(8).styleModifier {
                property("position", "absolute")
                property("right", "0")
                property("top", "50%")
                property("transform", "translateY(-50%)")
            },
            verticalAlignment = Alignment.CenterVertically
        ) {
            AddEventButton(onClick = onAddEvent)
            MapButton(onClick = onMapClick)
        }

        // Spacer establishes row height to match the action button pill.
        Box(modifier = Modifier.sHeight(48))
    }
}

@Composable
private fun ClubsEventsSwitch(activeTab: String = "Events", onTabSelected: (String) -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .sHeight(44)
            .sBorderRadius(34)
            .sPadding(2, 4)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property("background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                    "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                    "${AppGradients.ButtonBorderGradientPrimary} border-box")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        listOf("Events").forEach { tab -> // "Clubs" disabled
            val isActive = tab == activeTab
            Box(
                modifier = Modifier
                    .sHeight(36)
                    .sBorderRadius(22)
                    .sPadding(8, 26)
                    .then(
                        if (isActive) Modifier.backgroundColor(sportColors.primary)
                        else Modifier
                    )
                    .cursor(Cursor.Pointer)
                    .onClick { onTabSelected(tab) },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (tab == "Events") t.pageEventsTab else t.pageClubsTab,
                    modifier = Modifier
                        .bodyLMedium()
                        .color(if (isActive) sportColors.onPrimary else AppColors.Text.Secondary)
                )
            }
        }
    }
}

// =============================================================================
// Inline Filter Pills (center of action bar)
// =============================================================================

@Composable
private fun ActionBarFilterPills(
    activeTab: String,
    cities: List<String>,
    cityFlags: Map<String, String> = emptyMap(),
    selectedCity: String?,
    onCitySelected: (String?) -> Unit,
    citiesReady: Boolean,
    communities: List<ChatListItemDto> = emptyList(),
    selectedCommunityId: String? = null,
    selectedCommunityName: String? = null,
    onCommunitySelected: (String?) -> Unit = {},
    onCommunityFilterOpen: () -> Unit = {},
    selectedDate: String? = null,
    onDateSelected: (String?) -> Unit = {}
) {
    val t = LocalAppStrings.current
    // Figma 8438:274715 — outer surface #02040b, 2px vertical padding, 8px gap,
    // each pill sits on a translucent navy underlay with the project gradient border.
    Row(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(2, 0)
            .sGap(8)
            .styleModifier {
                property("background", "#02040b")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Location — always visible, shows "All cities" until cities load.
        // Uses the sport-style panel (round flag chips + globe for "All cities").
        FilterPillDropdown(
            label = t.pageFilterLocation,
            options = cities,
            optionPrefixes = cities.map { cityFlags[it].orEmpty() },
            selectedValue = selectedCity,
            allLabel = t.dropdownAllCities,
            onSelected = onCitySelected,
            useCityPanel = true
        )

        // Events-only filters
        if (activeTab == "Events") {
            // Until chats are loaded, synthesize a one-item list from the
            // persisted selection so the chip can show the community name
            // immediately after reload (otherwise the dropdown would fall back
            // to "All" while the filter is in fact active).
            val communityOptions: List<String>
            val communityValues: List<String>
            if (communities.isNotEmpty()) {
                communityOptions = communities.map { it.name ?: t.pageUnnamedCommunity }
                communityValues = communities.map { it.id }
            } else if (selectedCommunityId != null) {
                communityOptions = listOf(selectedCommunityName ?: t.pageFilterCommunity)
                communityValues = listOf(selectedCommunityId)
            } else {
                communityOptions = emptyList()
                communityValues = emptyList()
            }
            FilterPillDropdown(
                label = t.pageFilterCommunity,
                options = communityOptions,
                values = communityValues,
                selectedValue = selectedCommunityId,
                allLabel = t.pageFilterAll,
                onSelected = onCommunitySelected,
                onOpen = onCommunityFilterOpen,
            )

            FilterPillDate(
                label = t.pageFilterCalendar,
                selectedDate = selectedDate,
                onDateSelected = onDateSelected
            )
        }
    }
}

@Composable
private fun FilterPillDropdown(
    label: String,
    options: List<String>,
    values: List<String>? = null,
    optionPrefixes: List<String>? = null,
    selectedValue: String?,
    allLabel: String,
    onSelected: (String?) -> Unit,
    onOpen: () -> Unit = {},
    // When true, the expanded menu renders the sport-style LocationDropdownPanel
    // (round flag chips + globe "All cities" row) instead of the plain text list.
    useCityPanel: Boolean = false,
) {
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }
    val selectedIndex = if (values != null) values.indexOf(selectedValue)
        else options.indexOf(selectedValue)
    val selectedLabel = if (selectedIndex >= 0) options[selectedIndex] else null
    val selectedPrefix = optionPrefixes?.getOrNull(selectedIndex).orEmpty()

    // Stable DOM id so we can detect clicks outside this dropdown — the
    // fixed-position backdrop trick fails here because the action bar's
    // ancestor uses `transform`, which traps `position: fixed` inside it.
    val dropdownId = remember { "fpd-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    // -1 = nothing hovered, -2 = "All" option, >= 0 = options[i]
    var hoveredIndex by remember { mutableStateOf(-1) }

    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose {
            kotlinx.browser.document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        // Pill trigger — Figma 9508:162429, h 44, px 22, py 10.
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(100)
                .sPadding(10, 22)
                .cursor(Cursor.Pointer)
                .onClick {
                    val nowOpen = !expanded
                    expanded = nowOpen
                    if (nowOpen) onOpen()
                }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #0B1628, #0B1628) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                },
            contentAlignment = Alignment.Center
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "$label: ",
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                        .styleModifier { property("white-space", "nowrap") }
                )
                if (selectedLabel != null && selectedPrefix.isNotEmpty()) {
                    SpanText(
                        selectedPrefix,
                        modifier = Modifier.bodyLMedium()
                            .sPaddingRight(6)
                            .styleModifier {
                                property("white-space", "nowrap")
                                property("line-height", "1")
                                property("transform", "translateY(1px)")
                            }
                    )
                }
                SpanText(
                    selectedLabel ?: allLabel,
                    modifier = Modifier.bodyLMedium().color(sportColors.primary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Dropdown
        if (expanded && useCityPanel) {
            com.arena_web.ui.components.dropdown.LocationDropdownPanel(
                selectedCity = selectedValue,
                cities = options,
                cityFlags = optionPrefixes
                    ?.let { prefixes -> options.indices.associate { options[it] to prefixes.getOrNull(it).orEmpty() } }
                    ?: emptyMap(),
                onSelect = { city ->
                    onSelected(city)
                    expanded = false
                },
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sLeft(0)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            )
        } else if (expanded) {
            Column(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sLeft(0)
                    .sMinWidth(200)
                    .sBorderRadius(16)
                    .sPadding(8)
                    .zIndex(100)
                    .styleModifier {
                        property("width", "max-content")
                        property("max-height", "260px")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            ) {
                // "All" option
                val allHovered = hoveredIndex == -2
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(10)
                        .sPadding(10, 14)
                        .sPaddingRight(24)
                        .cursor(Cursor.Pointer)
                        .styleModifier {
                            property(
                                "background",
                                when {
                                    selectedValue == null -> "rgba(0, 97, 255, 0.12)"
                                    allHovered -> "rgba(255, 255, 255, 0.06)"
                                    else -> "transparent"
                                }
                            )
                        }
                        .onMouseEnter { hoveredIndex = -2 }
                        .onMouseLeave { if (hoveredIndex == -2) hoveredIndex = -1 }
                        .onClick {
                            onSelected(null)
                            expanded = false
                        }
                ) {
                    SpanText(
                        allLabel,
                        modifier = Modifier.bodyLMedium().color(
                            if (selectedValue == null) sportColors.primary else AppColors.Text.Secondary
                        )
                    )
                }

                options.forEachIndexed { index, option ->
                    val value = values?.getOrNull(index) ?: option
                    val isSelected = value == selectedValue
                    val isHovered = hoveredIndex == index
                    val prefix = optionPrefixes?.getOrNull(index).orEmpty()
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sBorderRadius(10)
                            .sPadding(10, 14)
                            .sPaddingRight(24)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property(
                                    "background",
                                    when {
                                        isSelected -> "rgba(0, 97, 255, 0.12)"
                                        isHovered -> "rgba(255, 255, 255, 0.06)"
                                        else -> "transparent"
                                    }
                                )
                            }
                            .onMouseEnter { hoveredIndex = index }
                            .onMouseLeave { if (hoveredIndex == index) hoveredIndex = -1 }
                            .onClick {
                                onSelected(value)
                                expanded = false
                            }
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.px)
                        ) {
                            if (prefix.isNotEmpty()) {
                                SpanText(
                                    prefix,
                                    modifier = Modifier.bodyLMedium()
                                        .styleModifier {
                                            property("white-space", "nowrap")
                                            property("line-height", "1")
                                            property("transform", "translateY(1px)")
                                        }
                                )
                            }
                            SpanText(
                                option,
                                modifier = Modifier.bodyLMedium().color(
                                    if (isSelected) sportColors.primary else AppColors.Text.Secondary
                                ).styleModifier { property("white-space", "nowrap") }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FilterPillDate(
    label: String,
    selectedDate: String?,
    onDateSelected: (String?) -> Unit
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by remember { mutableStateOf(false) }

    // Format date for display
    val displayValue = if (selectedDate != null) {
        try {
            val parts = selectedDate.split("-")
            "${parts[2]}/${parts[1]}"
        } catch (_: Exception) { selectedDate }
    } else t.pageFilterAll

    // Same trick as FilterPillDropdown — `position:fixed` backdrop is trapped
    // by an ancestor `transform`, so we listen on document instead.
    val dropdownId = remember { "fpd-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}" }
    DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) {
                expanded = false
            }
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose {
            kotlinx.browser.document.removeEventListener("mousedown", handler)
        }
    }

    Box(modifier = Modifier.id(dropdownId).position(Position.Relative)) {
        // Pill trigger — Figma 9508:162431, h 44, px 22, py 10.
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(100)
                .sPadding(10, 22)
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded }
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property("background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #0B1628, #0B1628) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box")
                },
            contentAlignment = Alignment.Center
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    "$label: ",
                    modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
                        .styleModifier { property("white-space", "nowrap") }
                )
                SpanText(
                    displayValue,
                    modifier = Modifier.bodyLMedium().color(sportColors.primary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Date picker dropdown — a #09142d rounded card matching the Location
        // dropdown (LocationDropdownPanel); the picker is rendered `bare` so the
        // card is the only surface.
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .sTop(52)
                    .sRight(0)
                    .zIndex(100)
                    .sBorderRadius(20)
                    .sPadding(12, 12)
                    .styleModifier {
                        property("min-width", "max-content")
                        property("box-sizing", "border-box")
                        property("background", "#09142d")
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
                    .onClick { it.stopPropagation() }
            ) {
                DatePicker(
                    selectedDate = selectedDate ?: "",
                    onDateSelected = { date ->
                        onDateSelected(date)
                        expanded = false
                    },
                    bare = true,
                    // Desktop filter: classic 7-day window paged by the arrows
                    // (no scroll strip), so the dropdown always shows 7 across.
                    scrollable = false
                )
            }
        }
    }
}

@Composable
private fun AddEventButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(16)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(10, 24)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.pageAddEvent,
                modifier = Modifier
                    .bodyLMedium()
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun AddClubButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .position(Position.Relative)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .sSize(44)
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary)
                .overflow(Overflow.Hidden)
                .zIndex(1),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                src = AppIcons.Add,
                modifier = Modifier.styleModifier {
                    property("filter", sportColors.iconFilterOnPrimary)
                }
            )
        }
        Box(
            modifier = Modifier
                .sWidth(7)
                .sHeight(16)
                .backgroundColor(sportColors.primary)
                .sMarginLeft(-4)
                .zIndex(0)
        )
        Box(
            modifier = Modifier
                .sHeight(44)
                .sBorderRadius(22)
                .backgroundColor(sportColors.primary)
                .sPadding(10, 24)
                .sMarginLeft(-4),
            contentAlignment = Alignment.Center
        ) {
            SpanText(
                t.pageAddClub,
                modifier = Modifier
                    .bodyLMedium()
                    .color(sportColors.onPrimary)
                    .styleModifier { property("white-space", "nowrap") }
            )
        }
    }
}

@Composable
private fun MapButton(onClick: () -> Unit = {}) {
    val t = LocalAppStrings.current
    // Figma 8750:342078 — outlined pill, subtle Arena-blue gradient fill,
    // project gradient border, map icon + "Map" label.
    Row(
        modifier = Modifier
            .sBorderRadius(28)
            .sPadding(10, 24)
            .sGap(8)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("box-sizing", "border-box")
            }
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        Icon(src = AppIcons.Map, modifier = Modifier.sSize(18))
        SpanText(
            t.pageMap,
            modifier = Modifier.bodyLMedium().color(AppColors.Text.Secondary)
        )
    }
}

// =============================================================================
// Event Grid
// =============================================================================

private const val EVENT_ROW_HEIGHT = "calc((100vh - 247 * var(--scale)) / 2)"
private const val EVENT_GRID_SCROLL_ID = "event-grid-scroll"
private const val EVENT_GRID_SCROLL_KEY = "arena_desktop_events_scroll_top"

@Composable
private fun EventGrid(
    events: List<ArenaEvent>,
    isLoadingMore: Boolean = false,
    onEventClick: (ArenaEvent) -> Unit = {},
    onLoadMore: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    var restoredScroll by remember { mutableStateOf(false) }

    // Restore the previous scroll once events are rendered (e.g. on return
    // from the event-detail page). Fires once per fresh mount with content.
    LaunchedEffect(events.isNotEmpty()) {
        if (restoredScroll || events.isEmpty()) return@LaunchedEffect
        val saved = kotlinx.browser.window.sessionStorage.getItem(EVENT_GRID_SCROLL_KEY)
            ?.toDoubleOrNull() ?: return@LaunchedEffect
        // Wait one frame so the cards are painted before we set scrollTop —
        // otherwise scrollHeight is still 0 and the assignment is clamped.
        kotlinx.browser.window.requestAnimationFrame {
            kotlinx.browser.document.getElementById(EVENT_GRID_SCROLL_ID)?.scrollTop = saved
            restoredScroll = true
        }
    }

    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(EVENT_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        var savePending = false
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
            // Throttle the sessionStorage write to one per animation frame —
            // scroll fires per-pixel and synchronous writes would stutter
            // mid-scroll on low-end devices.
            if (!savePending) {
                savePending = true
                kotlinx.browser.window.requestAnimationFrame {
                    kotlinx.browser.window.sessionStorage.setItem(
                        EVENT_GRID_SCROLL_KEY,
                        el.scrollTop.toString()
                    )
                    savePending = false
                }
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Column(modifier = modifier.id(EVENT_GRID_SCROLL_ID).sGap(20)) {
        // Hero row — first 2 events + center tagline
        if (events.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .position(Position.Relative)
                    .styleModifier {
                        property("height", EVENT_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                Row(
                    modifier = Modifier.fillMaxSize().sGap(20)
                ) {
                    // Left hero card
                    ArenaEventCard(
                        event = events[0],
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "31 1 0px") },
                        onClick = { onEventClick(events[0]) }
                    )

                    // Center spacer for tagline
                    Box(modifier = Modifier.styleModifier { property("flex", "38 1 0px") })

                    // Right hero card (or empty spacer to keep proportions)
                    if (events.size >= 2) {
                        ArenaEventCard(
                            event = events[1],
                            modifier = Modifier
                                .fillMaxHeight()
                                .styleModifier { property("flex", "31 1 0px") },
                            onClick = { onEventClick(events[1]) }
                        )
                    } else {
                        Box(modifier = Modifier.styleModifier { property("flex", "31 1 0px") })
                    }
                }

                // Center tagline overlay
                if (events.size >= 2) {
                    Column(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .top(0.px).bottom(0.px)
                            .left(0.px).right(0.px)
                            .sPaddingTop(40)
                            .styleModifier { property("pointer-events", "none") },
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Top
                    ) {
                        SpanText(
                            t.pageOwnTheArena,
                            modifier = Modifier
                                .sFontSize(46)
                                .fontWeight(FontWeight.SemiBold)
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("line-height", "46px")
                                    property("letter-spacing", "0.46px")
                                    property("text-align", "center")
                                }
                        )
                        SpanText(
                            t.pageArenaBannerSubtitle,
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(FontWeight.Normal)
                                .color(AppColors.Text.Secondary)
                                .sPaddingTop(8)
                                .styleModifier {
                                    property("line-height", "26px")
                                    property("letter-spacing", "0.22px")
                                    property("text-align", "center")
                                }
                        )
                    }
                }
            }
        }

        // Regular rows — 4 columns, events from index 2 onward
        val remainingEvents = events.drop(2)
        remainingEvents.chunked(4).forEach { rowEvents ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(20)
                    .styleModifier {
                        property("height", EVENT_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                rowEvents.forEach { event ->
                    ArenaEventCard(
                        event = event,
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "1 1 0px") },
                        onClick = { onEventClick(event) }
                    )
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().sPadding(20),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.pageLoadingMoreEvents,
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

// =============================================================================
// Empty State
// =============================================================================

@Composable
private fun EmptyEventsState(onAddEvent: () -> Unit = {}) {
    EventsEmptyState(onAddEvent = onAddEvent)
}

// =============================================================================
// Club Grid
// =============================================================================

private const val CLUB_ROW_HEIGHT = "calc((100vh - 247 * var(--scale)) / 2)"
private const val CLUB_GRID_SCROLL_ID = "club-grid-scroll"

@Composable
private fun ClubGrid(
    clubs: List<ClubDto>,
    isLoadingMore: Boolean = false,
    onClubClick: (ClubDto) -> Unit = {},
    onLoadMore: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val currentOnLoadMore by rememberUpdatedState(onLoadMore)
    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(CLUB_GRID_SCROLL_ID)
            ?: return@DisposableEffect onDispose {}
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            val scrollTop = el.scrollTop
            val clientHeight = el.clientHeight
            val scrollHeight = el.scrollHeight
            if (scrollTop + clientHeight >= scrollHeight - 300) {
                currentOnLoadMore()
            }
        }
        el.addEventListener("scroll", listener)
        onDispose { el.removeEventListener("scroll", listener) }
    }

    Column(modifier = modifier.id(CLUB_GRID_SCROLL_ID).sGap(20)) {
        // Hero row — first 2 clubs + center tagline
        if (clubs.isNotEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .position(Position.Relative)
                    .styleModifier {
                        property("height", CLUB_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                Row(
                    modifier = Modifier.fillMaxSize().sGap(20)
                ) {
                    // Left hero card
                    ClubCard(
                        club = clubs[0],
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "31 1 0px") },
                        onClick = { onClubClick(clubs[0]) }
                    )

                    // Center spacer for tagline
                    Box(modifier = Modifier.styleModifier { property("flex", "38 1 0px") })

                    // Right hero card
                    if (clubs.size >= 2) {
                        ClubCard(
                            club = clubs[1],
                            modifier = Modifier
                                .fillMaxHeight()
                                .styleModifier { property("flex", "31 1 0px") },
                            onClick = { onClubClick(clubs[1]) }
                        )
                    }
                }

                // Center tagline overlay
                if (clubs.size >= 2) {
                    Column(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .top(0.px).bottom(0.px)
                            .left(0.px).right(0.px)
                            .sPaddingTop(40)
                            .styleModifier { property("pointer-events", "none") },
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Top
                    ) {
                        SpanText(
                            t.pageOwnTheArena,
                            modifier = Modifier
                                .sFontSize(46)
                                .fontWeight(FontWeight.SemiBold)
                                .color(AppColors.Text.Secondary)
                                .styleModifier {
                                    property("line-height", "46px")
                                    property("letter-spacing", "0.46px")
                                    property("text-align", "center")
                                }
                        )
                        SpanText(
                            t.pageArenaBannerSubtitle,
                            modifier = Modifier
                                .sFontSize(22)
                                .fontWeight(FontWeight.Normal)
                                .color(AppColors.Text.Secondary)
                                .sPaddingTop(8)
                                .styleModifier {
                                    property("line-height", "26px")
                                    property("letter-spacing", "0.22px")
                                    property("text-align", "center")
                                }
                        )
                    }
                }
            }
        }

        // Regular rows — 4 columns, clubs from index 2 onward
        val remainingClubs = clubs.drop(2)
        remainingClubs.chunked(4).forEach { rowClubs ->
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(20)
                    .styleModifier {
                        property("height", CLUB_ROW_HEIGHT)
                        property("flex-shrink", "0")
                    }
            ) {
                rowClubs.forEach { club ->
                    ClubCard(
                        club = club,
                        modifier = Modifier
                            .fillMaxHeight()
                            .styleModifier { property("flex", "1 1 0px") },
                        onClick = { onClubClick(club) }
                    )
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().sPadding(20),
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.pageLoadingMoreClubs,
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

// =============================================================================
// Club Card
// =============================================================================

@Composable
private fun ClubCard(
    club: ClubDto,
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val imageUrl = club.mainPhotoUrl ?: "/icons/event_football_field.png"

    Box(
        modifier = modifier
            .overflow(Overflow.Hidden)
            .sBorderRadius(16)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("border", "0.5px solid rgba(255,255,255,0.4)")
                property("background",
                    "url('$imageUrl') center/cover no-repeat")
            }
    ) {
        // Bottom gradient overlay
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px).right(0.px)
                .styleModifier {
                    property("height", "40%")
                    property("background", "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)")
                }
        )

        // Bottom content: name/address + Book button
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Absolute)
                .bottom(0.px).left(0.px).right(0.px)
                .sPadding(18)
                .sGap(12),
            verticalAlignment = Alignment.Bottom,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            // Left: club name + address
            Column(
                modifier = Modifier.sGap(4).styleModifier { property("flex", "1 1 0px") }
            ) {
                SpanText(
                    club.name,
                    modifier = Modifier
                        .sFontSize(26)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier { property("white-space", "nowrap") }
                )
                Row(
                    modifier = Modifier.sGap(4),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        src = AppIcons.Location,
                        modifier = Modifier.sSize(16)
                    )
                    SpanText(
                        club.address,
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(FontWeight.Normal)
                            .color(KobwebColor.hex("B4CCF6"))
                    )
                }
            }

            // Right: Book button
            Row(
                modifier = Modifier
                    .backgroundColor(sportColors.primary)
                    .sHeight(44)
                    .sBorderRadius(22)
                    .sPaddingLeft(36)
                    .sPaddingRight(4)
                    .sPaddingTop(4)
                    .sPaddingBottom(4)
                    .sGap(10)
                    .styleModifier { property("flex-shrink", "0") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    t.pageBook,
                    modifier = Modifier
                        .bodyLMedium()
                        .color(sportColors.onPrimary)
                        .styleModifier { property("white-space", "nowrap") }
                )
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .overflow(Overflow.Hidden)
                        .backgroundColor(sportColors.secondary),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        src = AppIcons.ArrowUpOutline,
                        modifier = Modifier.styleModifier {
                            property("transform", "rotate(45deg)")
                            property("filter", sportColors.iconFilterOnSecondary)
                        }
                    )
                }
            }
        }
    }
}

// =============================================================================
// Empty Clubs State
// =============================================================================

@Composable
private fun EmptyClubsState() {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier.sGap(24),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Box(
                modifier = Modifier
                    .sSize(96)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 27%, rgba(3, 87, 230, 0.12) 68%) padding-box, " +
                                "linear-gradient(0deg, #060E1E, #060E1E) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                Icon(src = AppIcons.Location, modifier = Modifier.sSize(40))
            }

            Column(
                modifier = Modifier.sGap(8),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    t.pageNoClubsYet,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.Text.Primary)
                )
                SpanText(
                    t.pageClubsWillAppear,
                    modifier = Modifier.bodyLRegular().color(AppColors.Text.Secondary)
                )
            }
        }
    }
}

// =============================================================================
// Map Popup
// =============================================================================

private const val MAP_POPUP_CONTAINER_ID = "arena-map-popup"

@Composable
private fun MapPopup(
    activeTab: String,
    events: List<ArenaEvent>,
    clubs: List<ClubDto>,
    onClose: () -> Unit,
    onEventOpen: (ArenaEvent) -> Unit = {}
) {
    val t = LocalAppStrings.current
    val title = if (activeTab == "Events") t.pageEventsMapTitle else t.pageClubsMapTitle

    // Build markers JSON — events carry the full mini-card payload (photo,
    // date/time, title, location, members, cost) for the marker popups.
    val markersJson = remember(activeTab, events, clubs, t) {
        val arr = buildJsonArray {
            if (activeTab == "Events") {
                events.forEach { event ->
                    if (event.latitude.isNotBlank() && event.longitude.isNotBlank()) {
                        add(buildJsonObject {
                            put("id", event.id)
                            put("lat", event.latitude)
                            put("lng", event.longitude)
                            put("datetime", "${event.date} • ${event.time}")
                            put("title", event.title.ifBlank { event.sportLabel })
                            put("subtitle", event.location)
                            put("image", event.imageUrl)
                            mapMembersText(t, event)?.let { put("members", it) }
                            event.cost?.let { put("cost", it) }
                        })
                    }
                }
            } else {
                clubs.forEach { club ->
                    if (club.latitude != null && club.longitude != null) {
                        add(buildJsonObject {
                            put("lat", club.latitude.toString())
                            put("lng", club.longitude.toString())
                            put("title", club.name)
                            put("subtitle", club.address)
                            if (club.mainPhotoUrl != null) {
                                put("image", club.mainPhotoUrl)
                            }
                        })
                    }
                }
            }
        }
        Json.encodeToString(arr)
    }

    // Init map + markers
    LaunchedEffect(markersJson) {
        kotlinx.coroutines.delay(100)
        LeafletMap.initReadOnly(MAP_POPUP_CONTAINER_ID, 46.0, 15.0, 4)
        LeafletMap.addMarkers(markersJson)
        // Marker tap → centre that event's card in the floating strip.
        LeafletMap.onMarkerTap { id ->
            val container = document.getElementById(MAP_CARDS_CONTAINER_ID) as? HTMLElement
            val card = document.getElementById("arena-map-card-$id") as? HTMLElement
            if (container != null && card != null) {
                val target = card.offsetLeft - (container.clientWidth - card.offsetWidth) / 2
                val opts = js("({})")
                opts.left = target
                opts.behavior = "smooth"
                container.asDynamic().scrollTo(opts)
            }
        }
        // Popup mini-card click → event details.
        LeafletMap.onMarkerOpen { id ->
            events.firstOrNull { it.id == id }?.let(onEventOpen)
        }
    }

    // Cleanup on dispose
    DisposableEffect(Unit) {
        onDispose { LeafletMap.destroy() }
    }

    // Mobile: show the map as a bottom sheet.
    if (isMobile()) {
        com.arena_web.ui.components.overlay.BottomSheet(onDismiss = onClose, contentGap = 12, fullHeight = true) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(12)
                    .styleModifier {
                        property("flex", "1")
                        property("min-height", "0")
                    }
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        title,
                        modifier = Modifier.sFontSize(18).fontWeight(FontWeight.Medium).color(AppColors.Text.Secondary)
                    )
                    Box(
                        modifier = Modifier
                            .sSize(32)
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { onClose() }
                            .styleModifier {
                                property("background", "rgba(255, 255, 255, 0.08)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(src = AppIcons.Close)
                    }
                }
                // Map fills the rest of the sheet; the event card strip floats
                // over its bottom edge (map as an alternative event list).
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .position(Position.Relative)
                        .styleModifier { property("min-height", "0") }
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .sBorderRadius(16)
                            .overflow(Overflow.Hidden)
                            .id(MAP_POPUP_CONTAINER_ID)
                    )
                    if (activeTab == "Events") {
                        MapEventCardsStrip(events = events, onEventOpen = onEventOpen)
                    }
                }
                // Bottom gap so the map isn't flush with the sheet edge.
                Box(modifier = Modifier.fillMaxWidth().sHeight(16))
            }
        }
        return
    }

    // Desktop overlay
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .styleModifier { property("background-color", "rgba(0, 0, 0, 0.6)") }
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Popup card
        Column(
            modifier = Modifier
                .sBorderRadius(20)
                .overflow(Overflow.Hidden)
                .onClick { it.stopPropagation() }
                .animation(
                    PopupSlideUp.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
                .styleModifier {
                    property("width", "90vw")
                    property("height", "85vh")
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(10, 15, 30, 0.98) 0%, rgba(5, 8, 18, 0.98) 100%) padding-box, " +
                            "linear-gradient(0deg, #0A0D14, #0A0D14) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // Header
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(16, 24),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    title,
                    modifier = Modifier
                        .sFontSize(22)
                        .fontWeight(FontWeight.SemiBold)
                        .color(AppColors.PrimaryWhite)
                )
                Box(
                    modifier = Modifier
                        .sSize(36)
                        .sBorderRadius(100)
                        .cursor(Cursor.Pointer)
                        .onClick { onClose() }
                        .styleModifier {
                            property("background", "rgba(255, 255, 255, 0.08)")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    Icon(src = AppIcons.Close)
                }
            }

            // Map container + floating event card strip.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .flexGrow(1)
                    .position(Position.Relative)
                    .styleModifier { property("min-height", "0") }
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .id(MAP_POPUP_CONTAINER_ID)
                )
                if (activeTab == "Events") {
                    MapEventCardsStrip(events = events, onEventOpen = onEventOpen)
                }
            }
        }
    }
}

// =============================================================================
// Map event card strip — the map as an alternative, browsable event list
// =============================================================================

private const val MAP_CARDS_CONTAINER_ID = "arena-map-cards"

private fun mapMembersText(t: AppStrings, event: ArenaEvent): String? = when {
    event.membersJoined != null && event.membersTotal != null ->
        t.arenaMembersJoinedTotal(event.membersJoined, event.membersTotal)
    event.membersJoined != null -> t.arenaMembersCount(event.membersJoined)
    event.membersTotal != null -> t.arenaMembersCount(event.membersTotal)
    else -> null
}

/**
 * Horizontal snap strip of mini event cards floating over the map's bottom
 * edge (mirrors the mobile app's pager). Marker tap centres that event's card;
 * settling on a card pans the map to its pin; clicking a card opens the event.
 */
@Composable
private fun MapEventCardsStrip(
    events: List<ArenaEvent>,
    onEventOpen: (ArenaEvent) -> Unit
) {
    val mapEvents = remember(events) {
        events.filter { it.latitude.isNotBlank() && it.longitude.isNotBlank() }
    }
    if (mapEvents.isEmpty()) return
    val mobile = isMobile()

    // Scroll settled (~150ms quiet) → pan the map to the centred card's pin.
    DisposableEffect(mapEvents) {
        val container = document.getElementById(MAP_CARDS_CONTAINER_ID) as? HTMLElement
        var timer = 0
        val listener: (org.w3c.dom.events.Event) -> Unit = {
            window.clearTimeout(timer)
            timer = window.setTimeout({
                val c = document.getElementById(MAP_CARDS_CONTAINER_ID) as? HTMLElement
                if (c != null) {
                    val centre = c.scrollLeft + c.clientWidth / 2.0
                    var best: HTMLElement? = null
                    var bestDist = Double.MAX_VALUE
                    for (i in 0 until c.children.length) {
                        val child = c.children.item(i) as? HTMLElement ?: continue
                        val dist = abs(child.offsetLeft + child.offsetWidth / 2.0 - centre)
                        if (dist < bestDist) {
                            bestDist = dist
                            best = child
                        }
                    }
                    val id = best?.id?.removePrefix("arena-map-card-")
                    val event = mapEvents.firstOrNull { it.id == id }
                    val lat = event?.latitude?.toDoubleOrNull()
                    val lng = event?.longitude?.toDoubleOrNull()
                    if (lat != null && lng != null) LeafletMap.setView(lat, lng, 14)
                }
            }, 150)
        }
        container?.addEventListener("scroll", listener)
        onDispose {
            window.clearTimeout(timer)
            container?.removeEventListener("scroll", listener)
        }
    }

    Row(
        modifier = Modifier
            .id(MAP_CARDS_CONTAINER_ID)
            .position(Position.Absolute)
            .left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .gap(8.px)
            .styleModifier {
                property("overflow-x", "auto")
                property("scroll-snap-type", "x mandatory")
                property("padding", "12px 24px")
                property("scrollbar-width", "none")
                property("-webkit-overflow-scrolling", "touch")
            }
    ) {
        mapEvents.forEach { event ->
            MapEventMiniCard(event = event, mobile = mobile, onClick = { onEventOpen(event) })
        }
    }
}

/** Compact event card for the map strip — photo + date • time / title / location / members · cost. */
@Composable
private fun MapEventMiniCard(
    event: ArenaEvent,
    mobile: Boolean,
    onClick: () -> Unit
) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .id("arena-map-card-${event.id}")
            .flexShrink(0)
            .gap(12.px)
            .padding(8.px)
            .borderRadius(16.px)
            .cursor(Cursor.Pointer)
            .onClick {
                it.stopPropagation()
                onClick()
            }
            .styleModifier {
                property("width", if (mobile) "calc(100% - 48px)" else "320px")
                property("box-sizing", "border-box")
                property("scroll-snap-align", "center")
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "linear-gradient(0deg, #02040B, #02040B) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box"
                )
                property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.45)")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Photo — square thumb; football-field fallback like the list cards.
        Box(
            modifier = Modifier
                .size(88.px)
                .flexShrink(0)
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    if (event.imageUrl.isNotBlank()) {
                        property("background", "url('${event.imageUrl}') center / cover no-repeat")
                    } else {
                        property("background", "#0F172A")
                    }
                },
            contentAlignment = Alignment.Center
        ) {
            if (event.imageUrl.isBlank()) {
                Icon(
                    src = AppIcons.FootballField,
                    modifier = Modifier.size(32.px).styleModifier { property("opacity", "0.4") }
                )
            }
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .height(88.px)
                .styleModifier { property("min-width", "0") },
            verticalArrangement = Arrangement.SpaceBetween
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(3.px)
                    .styleModifier { property("min-width", "0") }
            ) {
                SpanText(
                    "${event.date} • ${event.time}",
                    modifier = Modifier
                        .fontSize(12.px)
                        .fontWeight(500)
                        .lineHeight(14.px)
                        .color(AppColors.PrimaryWhite)
                )
                SpanText(
                    event.title.ifBlank { event.sportLabel },
                    modifier = Modifier
                        .fillMaxWidth()
                        .fontSize(16.px)
                        .fontWeight(500)
                        .lineHeight(18.px)
                        .color(AppColors.Text.Primary)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        }
                )
                if (event.location.isNotBlank()) {
                    SpanText(
                        event.location,
                        modifier = Modifier
                            .fillMaxWidth()
                            .fontSize(12.px)
                            .lineHeight(14.px)
                            .color(AppColors.Text.Subtle)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                    )
                }
            }

            Row(modifier = Modifier.gap(14.px), verticalAlignment = Alignment.CenterVertically) {
                mapMembersText(t, event)?.let { members ->
                    Row(modifier = Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                        Icon(src = AppIcons.People, modifier = Modifier.size(16.px))
                        SpanText(
                            members,
                            modifier = Modifier
                                .fontSize(12.px)
                                .fontWeight(500)
                                .color(AppColors.Text.Secondary)
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
                event.cost?.let { cost ->
                    Row(modifier = Modifier.gap(6.px), verticalAlignment = Alignment.CenterVertically) {
                        Icon(src = AppIcons.Card, modifier = Modifier.size(16.px))
                        SpanText(
                            cost,
                            modifier = Modifier
                                .fontSize(12.px)
                                .fontWeight(500)
                                .color(AppColors.Text.Secondary)
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
            }
        }
    }
}
